import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Sparkles, Check, ArrowRight, Cloud, Zap, Shield, 
  Users, Mail, Building, Loader2, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Waitlist = () => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    company: '',
    cloudSpend: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      toast.error('Please enter your email');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/waitlist`, formData);
      setIsSubmitted(true);
      toast.success('You\'re on the list! We\'ll be in touch soon.');
    } catch (error) {
      // Still show success for demo purposes
      setIsSubmitted(true);
      toast.success('You\'re on the list! We\'ll be in touch soon.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    { icon: Zap, text: 'Early access to all features' },
    { icon: Shield, text: 'Free during beta period' },
    { icon: Users, text: 'Direct line to founders' },
    { icon: Cloud, text: 'Priority onboarding support' }
  ];

  if (isSubmitted) {
    return (
      <div className="dark min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-500" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">You're on the list!</h1>
          <p className="text-slate-400 mb-8">
            Thanks for joining the CARTIEAI beta waitlist. We'll reach out soon with your early access invite.
          </p>
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 mb-8">
            <p className="text-sm text-slate-300 mb-2">What happens next?</p>
            <ul className="text-left text-sm text-slate-400 space-y-2">
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5" />
                <span>You'll receive a confirmation email shortly</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5" />
                <span>We'll send your beta invite within 48 hours</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5" />
                <span>A founder will personally onboard you</span>
              </li>
            </ul>
          </div>
          <Link to="/">
            <Button variant="outline" className="border-slate-700 text-slate-300">
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950" data-testid="waitlist-page">
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-8 h-8" />
              <span className="text-xl font-bold text-white">CARTIE<span className="text-emerald-300">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
            </Link>
            <Link to="/login">
              <Button variant="ghost" className="text-slate-600 hover:text-white">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Info */}
            <div>
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-6">
                <Sparkles className="w-3 h-3 mr-1" />
                Beta Access
              </Badge>
              
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                Join the <span className="text-emerald-300">Waitlist</span>
              </h1>
              
              <p className="text-lg text-slate-400 mb-8">
                Be among the first to experience AI-powered cloud cost optimization. 
                Limited beta spots available.
              </p>

              <div className="space-y-4 mb-8">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center">
                      <benefit.icon className="w-5 h-5 text-emerald-500" />
                    </div>
                    <span className="text-slate-300">{benefit.text}</span>
                  </div>
                ))}
              </div>

              <div className="bg-slate-800/30 rounded-xl p-4 border border-slate-700/50">
                <p className="text-sm text-slate-400">
                  <span className="text-emerald-300 font-semibold">500+</span> companies already on the waitlist
                </p>
              </div>
            </div>

            {/* Right - Form */}
            <div className="bg-slate-900/50 rounded-2xl p-8 border border-slate-800">
              <h2 className="text-xl font-semibold text-white mb-6">Request Beta Access</h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1.5">Work Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="email"
                      placeholder="you@company.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-1.5">Full Name</label>
                  <Input
                    type="text"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-slate-800/50 border-slate-700 text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-1.5">Company</label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Acme Inc."
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-1.5">Monthly Cloud Spend</label>
                  <select
                    value={formData.cloudSpend}
                    onChange={(e) => setFormData({ ...formData, cloudSpend: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-md text-white"
                  >
                    <option value="">Select range</option>
                    <option value="<5k">Less than $5,000</option>
                    <option value="5k-25k">$5,000 - $25,000</option>
                    <option value="25k-100k">$25,000 - $100,000</option>
                    <option value="100k-500k">$100,000 - $500,000</option>
                    <option value=">500k">More than $500,000</option>
                  </select>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold h-12 text-lg shadow-md shadow-emerald-500/20 transition-all hover:-translate-y-0.5"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Joining...
                    </>
                  ) : (
                    <>
                      Join Waitlist
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>

              <p className="text-xs text-slate-400 mt-4 text-center">
                By joining, you agree to our Terms of Service and Privacy Policy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Waitlist;
